// -------------------------------------------------------------------------
// Corrigido: Todos os imports no topo
// -------------------------------------------------------------------------

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import Menu from "../../comum/Menu/index.jsx"; 
import Footer from "../../comum/Footer/index.jsx"; 
import { api } from '../../services/api.js'; 

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc'; 

import './index.css'; 

// -------------------------------------------------------------------------
// Configuração do Day.js (CÓDIGO EXECUTÁVEL)
// -------------------------------------------------------------------------

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);
dayjs.extend(utc);


const Atestados = () => {
    const [atestados, setAtestados] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const chapa = localStorage.getItem("chapa");
    const navigate = useNavigate();

    // -------------------------------------------------------------------------
    // 1. LÓGICA DE INICIALIZAÇÃO E BUSCA
    // -------------------------------------------------------------------------

    useEffect(() => {
        if (!chapa) {
            navigate("/arbor/login");
        }
    }, [chapa, navigate]);

    // Função auxiliar para traduzir o status
    const getStatusName = (status) => {
        switch (status) {
            case 'A': return 'Aprovado';
            case 'N': return 'Negado';
            case 'I': default: return 'Em Análise';
        }
    };

    // Função para buscar a lista de atestados
    const fetchAtestados = async () => {
        if (!chapa) return;

        try {
            // Se estiver carregando pela primeira vez, mostra o modal de loading.
            if (isLoading) {
                 Swal.fire({
                    title: 'Carregando Atestados...',
                    text: 'Aguarde enquanto buscamos seus dados.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });
            }
            
            const res = await api.get(`/usuarios/${chapa}/atestados`);
            
            const dadosFormatados = res.data.map(item => {
                
                // Trata as datas ISO 8601 (ex: "2025-10-21T00:00:00.000Z")
                const datainicio = item.datainicio ? dayjs.utc(item.datainicio).format('DD/MM/YYYY') : '';
                const datafim = item.datafim ? dayjs.utc(item.datafim).format('DD/MM/YYYY') : '';
                const dataatestado = item.dataatestado ? dayjs.utc(item.dataatestado).format('DD/MM/YYYY') : '';

                // Lógica de extração de observação do campo 'motivo' ou campo 'observacao' do backend
                let motivoLimpo = item.motivo;
                let observacaoDoBackend = '';

                const match = item.motivo.match(/\s*\(Obs: (.*)\)$/);
                if (match) {
                    observacaoDoBackend = match[1];
                    motivoLimpo = item.motivo.replace(match[0], '').trim();
                } else if (item.observacao) {
                    observacaoDoBackend = item.observacao;
                }
                
                return {
                    ...item,
                    datainicio,
                    datafim,
                    dataatestado,
                    motivo: motivoLimpo, 
                    observacao: observacaoDoBackend, 
                    nomestatus: getStatusName(item.status)
                };
            });

            setAtestados(dadosFormatados || []);
            // Fecha o modal de loading (se estiver aberto)
            if (isLoading) {
                Swal.close();
            }
        } catch (err) {
            console.error('Erro ao buscar atestados:', err);
            Swal.close(); // Fecha o modal em caso de erro
            if (err.response && err.response.status === 404) {
                 setAtestados([]); 
            } else {
                 Swal.fire('Erro', 'Não foi possível carregar a lista de atestados.', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchAtestados();
    }, [chapa]);

    // Função de conversão de File para Base64
    const convertFileToBase64 = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null); 
        });
    };
    
    // Função para determinar o estilo do status
    const getStatusStyle = (status) => {
        switch (status) {
            case 'A': return { color: 'green', backgroundColor: '#e6ffe6', padding: '4px 8px', borderRadius: '4px' };
            case 'N': return { color: 'red', backgroundColor: '#ffe6e6', padding: '4px 8px', borderRadius: '4px' };
            case 'I': default: return { color: 'orange', backgroundColor: '#fffbe6', padding: '4px 8px', borderRadius: '4px' };
        }
    };

    // -------------------------------------------------------------------------
    // 2. MODAL DE CADASTRO E ENVIO (SWEETALERT2)
    // -------------------------------------------------------------------------

    const cadastrarNovoAtestado = async () => {
        let dataFimCalculada = ''; 

        const { value: formValues } = await Swal.fire({
            title: 'Cadastrar Novo Atestado',
            html: `
                <style>
                    /* Estilos para o Modal */
                    .swal2-content-custom { text-align: left; padding: 0 10px; }
                    .form-group-swal { margin-bottom: 15px; }
                    .form-group-swal label { display: block; font-weight: bold; margin-bottom: 5px; color: #333; }
                    .form-group-swal input[type="text"], .form-group-swal input[type="number"], .form-group-swal input[type="date"], .form-group-swal textarea {
                        width: 100%;
                        padding: 8px 12px;
                        border: 1px solid #ccc;
                        border-radius: 4px;
                        box-sizing: border-box;
                    }
                    .form-group-swal textarea { resize: vertical; min-height: 80px; }
                    
                    #swal-data-fim-display { 
                        display: block; 
                        padding: 8px 12px; 
                        background-color: #f0f0f0; 
                        border: 1px solid #ddd; 
                        border-radius: 4px; 
                        color: #555;
                        font-weight: normal;
                        text-align: center;
                    }

                    .file-input-wrapper { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
                    .file-input-label {
                        background-color: #007bff;
                        color: white;
                        padding: 8px 15px;
                        border-radius: 4px;
                        cursor: pointer;
                        text-align: center;
                        font-weight: 500;
                        flex-grow: 1; 
                    }
                </style>
                
                <div class="swal2-content-custom">
                    <div class="form-group-swal">
                        <label for="swal-data-inicio">Data do Atestado:</label>
                        <input id="swal-data-inicio" type="date" value="${dayjs().format('YYYY-MM-DD')}">
                    </div>
                    
                    <div class="form-group-swal">
                        <label for="swal-dias">Dias de Validade:</label>
                        <input id="swal-dias" type="number" min="1" placeholder="Dias de afastamento (Ex: 5)">
                    </div>

                    <div class="form-group-swal">
                        <label>Data de Fim Calculada (Afastamento):</label>
                        <span id="swal-data-fim-display">Será calculada automaticamente</span>
                    </div>

                    <div class="form-group-swal">
                        <label for="swal-motivo">Motivo Principal:</label>
                        <input id="swal-motivo" type="text" placeholder="Ex: Gripe, cirurgia">
                    </div>
                    
                    <div class="form-group-swal">
                        <label>Anexar Atestado (Foto/Arquivo):</label>
                        <div class="file-input-wrapper">
                            <input 
                                id="swal-foto-hidden" 
                                type="file" 
                                accept="image/*" 
                                capture="camera" 
                                style="display: none;"
                            >
                            <label for="swal-foto-hidden" class="file-input-label">
                                <i class="bi bi-camera-fill"></i> Scannear ou Anexar Foto
                            </label>
                            <span id="file-name-display" style="font-style: italic; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 120px;">Nenhum arquivo.</span>
                        </div>
                    </div>
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Enviar Atestado',
            cancelButtonText: 'Cancelar',
            didOpen: () => {
                const inputDias = document.getElementById('swal-dias');
                const inputDataInicio = document.getElementById('swal-data-inicio');
                const displayDataFim = document.getElementById('swal-data-fim-display');
                const inputFoto = document.getElementById('swal-foto-hidden');
                const fileNameDisplay = document.getElementById('file-name-display');

                const updateDataFim = () => {
                    const dataInicio = inputDataInicio.value;
                    const dias = parseInt(inputDias.value);

                    if (dataInicio && dias > 0) {
                        const dataFim = dayjs(dataInicio).add(dias - 1, 'day').format('DD/MM/YYYY');
                        displayDataFim.textContent = dataFim;
                        dataFimCalculada = dataFim;
                    } else {
                        displayDataFim.textContent = 'Será calculada automaticamente';
                        dataFimCalculada = '';
                    }
                };

                updateDataFim();
                inputDias.addEventListener('input', updateDataFim);
                inputDataInicio.addEventListener('change', updateDataFim);

                inputFoto.addEventListener('change', (event) => {
                    const fileName = event.target.files[0] ? event.target.files[0].name : 'Nenhum arquivo.';
                    fileNameDisplay.textContent = fileName;
                });
            },
            preConfirm: () => {
                const dataAtestado = document.getElementById('swal-data-inicio')?.value;
                const dias = parseInt(document.getElementById('swal-dias')?.value);
                const motivo = document.getElementById('swal-motivo')?.value.trim();
                // REMOVIDO: const observacao = document.getElementById('swal-obs')?.value.trim() || ''; 
                const fotoFile = document.getElementById('swal-foto-hidden')?.files[0];

                if (!dataAtestado || !dias || isNaN(dias) || dias <= 0 || !motivo) {
                    Swal.showValidationMessage('Preencha a Data, os Dias de Validade e o Motivo.');
                    return false;
                }
                if (!fotoFile) {
                    Swal.showValidationMessage('É obrigatório anexar a foto do atestado.');
                    return false;
                }

                if (!dataFimCalculada) {
                     Swal.showValidationMessage('Erro no cálculo da data final. Preencha os campos de Data e Dias.');
                     return false;
                }
                
                return {
                    inicio: dayjs(dataAtestado).format('DD/MM/YYYY'), 
                    fim: dataFimCalculada,
                    motivo: motivo, 
                    // REMOVIDO: observacao: observacao, 
                    fotoFile: fotoFile
                };
            }
        });

        if (formValues) {
            // Modal de Processamento de Envio
            Swal.fire({
                title: 'Processando Envio...',
                text: 'Convertendo a foto e enviando dados. Aguarde.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading(); 
                }
            });

            const base64Image = await convertFileToBase64(formValues.fotoFile);
            
            if (!base64Image) {
                Swal.fire('Erro', 'Falha na conversão da imagem. Tente novamente.', 'error');
                return;
            }

            try {
                // REMOVIDO: o campo observacao foi retirado do payload
                await api.post(`/usuarios/${chapa}/atestados`, {
                    inicio: formValues.inicio, 
                    fim: formValues.fim,
                    motivo: formValues.motivo,
                    // observacao: formValues.observacao, // REMOVIDO
                    imagem: base64Image.split(',')[1], 
                    acao: 'C' 
                });

                Swal.fire('Sucesso!', 'Atestado enviado para aprovação.', 'success');
                fetchAtestados(); 
            } catch (err) {
                console.error('Erro ao enviar atestado:', err.response || err);
                const errorMessage = err.response?.data?.erro || 'Erro desconhecido ao cadastrar o atestado.';
                Swal.fire('Erro', errorMessage, 'error');
            }
        }
    };
    
    // -------------------------------------------------------------------------
    // 3. RENDERIZAÇÃO DA TELA
    // -------------------------------------------------------------------------
    
    // O loading inicial agora é um modal (dentro do fetchAtestados),
    // então se isLoading for true, apenas não renderiza a lista.
    if (isLoading) {
        // Retornar um div vazio ou o Menu/Footer enquanto o loading está no modal
        return (
            <>
                <Menu modulo="ARBOR" />
                {/* Deixa a tela vazia. O loading real está no modal do fetchAtestados. */}
                <div className="container-fluid mt-5 pt-5 text-center">
                    {/* Se o modal falhar ou for bloqueado, este texto serve como fallback */}
                </div>
                <Footer />
            </>
        );
    }
    
    // Renderização após o carregamento (isLoading === false)
    return (
        <div>
            <Menu modulo="ARBOR" />
            
            <div className="container-fluid mt-5 pt-3"> 
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3>Meus Atestados</h3>
                    <button 
                        className="btn btn-primary"
                        onClick={cadastrarNovoAtestado}
                    >
                        <i className="bi bi-plus-circle"></i> Novo Atestado
                    </button>
                </div>

                {atestados.length === 0 ? (
                    <div className="alert alert-info text-center">
                        Nenhum atestado encontrado. Clique em "Novo Atestado" para enviar um.
                    </div>
                ) : (
                    atestados.map((item, index) => (
                        <div key={index} className="card mb-2">
                            <div
                                className="card-header d-flex flex-column"
                                style={{ cursor: 'default' }}
                            >
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    <span className="p-1">
                                        <strong className="info-data">
                                            Data Atestado: {item.dataatestado}
                                        </strong>
                                    </span>
                                    <span className="p-1">
                                        <strong className="info-motivo">
                                            Período Afast.: {item.datainicio} até {item.datafim}
                                        </strong>
                                    </span>
                                    <span className="p-1">
                                        <strong style={getStatusStyle(item.status)}>
                                            Status: {item.nomestatus}
                                        </strong>
                                    </span>
                                </div>
                                <div className="d-flex justify-content-start flex-wrap pt-2">
                                    <span className="p-1">
                                        <strong className="info-motivo">
                                            Motivo: {item.motivo}
                                        </strong>
                                    </span>
                                    {item.observacao && (
                                        <span className="p-1 text-muted small">
                                            (Obs: {item.observacao})
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
            <Footer className="footer navbar-fixed-bottom" />
        </div>
    );
};

export default Atestados;